import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import _get from 'lodash/get';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { Combobox as GenericCombobox } from 'components/Combobox';

import Label from '../Label/Label';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { makeValidators } from '../helpers';

const { block } = bem('hook-form-combobox');

function HookFormCombobox(props) {
  const {
    className,
    label,
    name,
    optional,
    defaultValue,
    children,
    required,
    showRequiredLabel,
    multiple,
    filterFn,
    closeOnSelect,
    autoFocus,
  } = props;

  const intl = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const validators = makeValidators(props, intl);
  const hasError = !!_get(errors, name, false);
  let resolvedDefault = '';
  if (defaultValue !== undefined) {
    resolvedDefault = defaultValue;
  } else if (multiple) {
    resolvedDefault = [];
  }

  return (
    <div className={block(className, { hasError })}>
      {label && (
        <Label
          name={name}
          message={label}
          optional={optional}
          required={required}
          showRequiredLabel={showRequiredLabel}
        />
      )}
      <Controller
        control={control}
        name={name}
        defaultValue={resolvedDefault}
        rules={validators}
        render={({ field: { value: fieldValue, onChange: handleFieldChange } }) => (
          <GenericCombobox
            value={fieldValue}
            onChange={handleFieldChange}
            multiple={multiple}
            filterFn={filterFn}
            closeOnSelect={closeOnSelect}
            autoFocus={autoFocus}>
            {children}
          </GenericCombobox>
        )}
      />
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
}

HookFormCombobox.propTypes = {
  autoFocus: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  closeOnSelect: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  filterFn: PropTypes.func,
  label: intlMessage,
  multiple: PropTypes.bool,
  name: PropTypes.string.isRequired,
  optional: PropTypes.bool,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  showRequiredLabel: PropTypes.bool,
};

export default HookFormCombobox;
